import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowRight } from 'lucide-react';
import { bookingsAPI, tenantAPI } from '../../services/api';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';

// ZATCA TLV QR Code generator (Phase 1 compliant)
const generateZatcaQrBase64 = (sellerName, vatNumber, timestamp, total, vatTotal) => {
  const toTlv = (tag, value) => {
    const encoder = new TextEncoder();
    const valueBytes = encoder.encode(String(value));
    return new Uint8Array([tag, valueBytes.length, ...valueBytes]);
  };
  const tlv1 = toTlv(1, sellerName);
  const tlv2 = toTlv(2, vatNumber);
  const tlv3 = toTlv(3, timestamp);
  const tlv4 = toTlv(4, total);
  const tlv5 = toTlv(5, vatTotal);
  const combined = new Uint8Array([...tlv1, ...tlv2, ...tlv3, ...tlv4, ...tlv5]);
  let binary = '';
  combined.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary);
};

const Invoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patientCredentials, setPatientCredentials] = useState(null);
  const [labSettings, setLabSettings] = useState({
    lab_name: '',
    lab_name_en: '',
    vat_number: '',
    phone: '',
    invoice_footer_image: null,
    invoice_thank_you_message: 'شكراً لاختياركم!',
    invoice_thank_you_message_en: 'Thank you for choosing us!'
  });
  // ZATCA-verified data from tenant (source of truth for legal documents)
  const [tenantData, setTenantData] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    // Load lab settings from localStorage for non-legal fields (footer, messages)
    const savedSettings = localStorage.getItem('labSettings');
    if (savedSettings) {
      setLabSettings(JSON.parse(savedSettings));
    }

    // Fetch ZATCA-verified tenant data (legal source of truth)
    const loadTenant = async () => {
      try {
        const res = await tenantAPI.getMyTenant();
        if (!isCancelled && res.data?.data) {
          setTenantData(res.data.data);
        }
      } catch (err) {
        console.error('Failed to load tenant data:', err);
      }
    };
    loadTenant();

    async function load() {
      if (!id || id === 'undefined' || id === 'null') {
        if (!isCancelled) navigate('/bookings');
        return;
      }
      try {
        const response = await bookingsAPI.getById(id);
        if (isCancelled) return;
        
        const b = response.data?.data;
        if (b?.payment_status !== 'paid') {
          navigate(`/bookings/${id}`);
          return;
        }
        // Contractor bookings are settled via the contractor account statement —
        // no ZATCA invoice is generated for them, so block direct access to this page.
        if (b?.visit_type === 'contractor') {
          navigate(`/bookings/${id}`);
          return;
        }
        setBooking(b);

        // Fetch patient access token for QR code via bookings endpoint (tenant-safe)
        try {
          const tokenResponse = await bookingsAPI.getPatientAccessToken(id);
          if (!isCancelled && tokenResponse.data?.data?.access_token) {
            setPatientCredentials({ access_token: tokenResponse.data.data.access_token });
          }
        } catch (credErr) {
          console.log('No patient access token found');
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching booking:', err);
      }
      if (!isCancelled) setLoading(false);
    }

    load();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getCustomerName = (customer) => {
    if (!customer) return '-';
    if (customer.full_name) return customer.full_name;
    const firstName = customer.first_name || '';
    const lastName = customer.last_name || '';
    return `${firstName} ${lastName}`.trim() || '-';
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">لم يتم العثور على الحجز</p>
      </div>
    );
  }

  const customer = booking.customer_id;
  
  // Get services (tests and packages)
  const services = [];
  const addedPackages = new Set();
  
  booking?.tests?.forEach(test => {
    if (test.package_id) {
      const pkgName = test.package_id?.name_ar || test.package_id?.name_en || '-';
      if (!addedPackages.has(pkgName)) {
        addedPackages.add(pkgName);
        services.push({
          name: pkgName,
          nameEn: test.package_id?.name_en || pkgName,
          type: 'باقة',
          typeEn: 'Package',
          price: test.price || 0
        });
      }
    } else {
      services.push({
        name: test.lab_test_id?.name_ar || test.lab_test_id?.name_en || '-',
        nameEn: test.lab_test_id?.name_en || '-',
        type: 'تحليل',
        typeEn: 'Test',
        price: test.price || 0
      });
    }
  });

  // Calculate amounts (single source of truth in UI)
  // - subtotal: before VAT, before discount
  // - discount is capped so it never produces negative totals
  const originalSubtotal = Math.max(0, booking?.total_amount || 0);
  const rawDiscount = Math.max(0, booking?.discount_amount || 0);
  const discountAmount = Math.min(rawDiscount, originalSubtotal);
  const subtotalAfterDiscount = Math.max(0, originalSubtotal - discountAmount);
  
  // Calculate VAT rate based on customer nationality
  const isSaudi = customer?.nationality === 'Saudi Arabia' || customer?.nationality === 'السعودية' || customer?.nationality === 'Saudi';
  const vatRate = isSaudi ? 0 : 15;
  const vatAmount = Math.max(0, Math.round((subtotalAfterDiscount * (vatRate / 100)) * 100) / 100);
  const totalAmount = Math.max(0, Math.round((subtotalAfterDiscount + vatAmount) * 100) / 100);
  const amountPaid = Math.max(0, booking?.paid_amount ?? totalAmount);

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-white shadow-md p-4 flex items-center justify-between sticky top-0 z-10">
        <button
          onClick={() => navigate(`/bookings/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
          العودة للحجز
        </button>
        <h1 className="text-xl font-bold text-gray-800">الفاتورة #{booking.booking_no}</h1>
        <button
          onClick={handlePrint}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700"
        >
          <Printer className="w-5 h-5" />
          طباعة
        </button>
      </div>

      {/* Printable Content */}
      <div className="max-w-3xl mx-auto bg-white shadow-lg my-8 print:my-0 print:shadow-none print:max-w-none">
        
        {/* Header */}
        <div className="p-6 border-b-2 border-gray-200">
          <div className="flex items-start justify-between">
            {/* ZATCA-Compliant QR Code */}
            <div className="flex-shrink-0">
              <QRCode 
                value={generateZatcaQrBase64(
                  tenantData?.zatcaProfile?.organizationName || tenantData?.name || labSettings.lab_name,
                  tenantData?.vatNumber || labSettings.vat_number,
                  new Date(booking?.booking_date).toISOString(),
                  totalAmount.toFixed(2),
                  vatAmount.toFixed(2)
                )} 
                size={80}
              />
            </div>
            
            {/* Logo & Title */}
            <div className="text-center flex-1 px-4">
              <img src={tenantData?.branding?.logoUrl || '/listech-logo.svg'} alt={tenantData?.name || labSettings.lab_name || 'Lab'} className="h-16 mx-auto mb-2" />
              <h1 className="text-xl font-bold text-gray-800">فاتورة ضريبية مبسطة</h1>
              <p className="text-sm text-gray-500">(Simplified Tax Invoice)</p>
            </div>
            
            {/* Empty space for balance */}
            <div className="w-20 flex-shrink-0"></div>
          </div>
        </div>

        {/* Invoice From / To */}
        <div className="p-6 grid grid-cols-2 gap-6 border-b border-gray-200">
          {/* Invoice From - Uses ZATCA-verified tenant data */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-700 mb-3 border-b pb-2">فاتورة من (Invoice From)</h3>
            <div className="space-y-2 text-sm">
              <p>
                <span className="font-semibold">اسم المنشأة (Organization): </span>
                <span>{tenantData?.zatcaProfile?.organizationName || tenantData?.name || labSettings.lab_name}</span>
              </p>
              <p>
                <span className="font-semibold">الفرع (Branch): </span>
                <span>{booking?.branch_id?.branch_name || tenantData?.zatcaProfile?.organizationUnitName || '-'}</span>
              </p>
              <p>
                <span className="font-semibold">الرقم الضريبي (VAT No.): </span>
                <span dir="ltr" className="font-mono">{tenantData?.vatNumber || labSettings.vat_number}</span>
              </p>
              {tenantData?.crNumber && (
              <p>
                  <span className="font-semibold">السجل التجاري (CR No.): </span>
                  <span dir="ltr" className="font-mono">{tenantData.crNumber}</span>
              </p>
              )}
            </div>
          </div>

          {/* Invoice To */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-700 mb-3 border-b pb-2">فاتورة إلى (Invoice To)</h3>
            <div className="space-y-2 text-sm">
              <p>
                <span className="font-semibold">اسم العميل (Customer Name): </span>
                <span>{getCustomerName(customer)}</span>
              </p>
              <p>
                <span className="font-semibold">رقم الهوية (ID No.): </span>
                <span>{customer?.national_id || '-'}</span>
              </p>
              <p>
                <span className="font-semibold">رقم الجوال (Mobile): </span>
                <span dir="ltr">{customer?.phone || '-'}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Booking & Payment Info */}
        <div className="p-6 grid grid-cols-2 gap-6 border-b border-gray-200">
          {/* Booking Info */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">معلومات الحجز (Booking Info)</h3>
            <div className="space-y-1 text-sm">
              <p>
                <span className="font-semibold">رقم الحجز (Booking ID): </span>
                <span>{booking?.booking_no}</span>
              </p>
              <p>
                <span className="font-semibold">اسم الموظف (Staff Name): </span>
                <span>{booking?.created_by_user_id?.name || 'النظام'}</span>
              </p>
            </div>
          </div>

          {/* Payment Info */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">معلومات الدفع (Payment Info)</h3>
            <div className="space-y-1 text-sm">
              <p>
                <span className="font-semibold">رقم الفاتورة (Invoice No.): </span>
                <span>REC{booking?.booking_no}</span>
              </p>
              <p>
                <span className="font-semibold">طريقة الدفع (Payment Method): </span>
                <span>{booking?.payment_method === 'cash' ? 'Cash' : booking?.payment_method || 'Cash'}</span>
              </p>
              <p>
                <span className="font-semibold">التاريخ والوقت (Date & Time): </span>
                <span dir="ltr">{new Date(booking?.booking_date).toLocaleDateString('en-GB')} {new Date(booking?.booking_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-right">
                  <div>اسم الخدمة</div>
                  <div className="text-xs text-gray-500 font-normal">(Service Name)</div>
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center">
                  <div>السعر الأصلي</div>
                  <div className="text-xs text-gray-500 font-normal">(Original Price)</div>
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center">
                  <div>السعر بعد الخصم</div>
                  <div className="text-xs text-gray-500 font-normal">(After Discount)</div>
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center">
                  <div>ضريبة القيمة المضافة ({vatRate}%)</div>
                  <div className="text-xs text-gray-500 font-normal">(VAT {vatRate}%)</div>
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center">
                  <div>الإجمالي</div>
                  <div className="text-xs text-gray-500 font-normal">(Total)</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, idx) => {
                // Calculate per-service discount (proportional)
                const safeServicePrice = Math.max(0, service.price || 0);
                const serviceDiscount = (discountAmount > 0 && originalSubtotal > 0)
                  ? (safeServicePrice / originalSubtotal) * discountAmount
                  : 0;
                const servicePriceAfterDiscount = Math.max(0, safeServicePrice - serviceDiscount);
                const serviceVat = Math.max(0, Math.round((servicePriceAfterDiscount * (vatRate / 100)) * 100) / 100);
                const serviceTotal = Math.max(0, Math.round((servicePriceAfterDiscount + serviceVat) * 100) / 100);
                return (
                  <tr key={idx}>
                    <td className="border border-gray-300 px-4 py-3">
                      <div>{service.name}</div>
                      <div className="text-xs text-gray-500">({service.type})</div>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">{safeServicePrice.toFixed(2)}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center">{servicePriceAfterDiscount.toFixed(2)}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center">{serviceVat.toFixed(2)}</td>
                    <td className="border border-gray-300 px-4 py-3 text-center">{serviceTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-4 flex justify-between">
            <div></div>
            <div className="w-80">
              <div className="flex justify-between py-2 border-b">
                <span className="font-semibold">
                  المجموع بعد الخصم{discountAmount > 0 ? ` (${booking?.coupon_code || 'كوبون'})` : ''}
                </span>
                <span>{subtotalAfterDiscount.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-semibold">ضريبة القيمة المضافة ({vatRate}%)</span>
                <span>{vatAmount.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between py-3 bg-gray-100 px-2 font-bold text-lg">
                <span>المجموع الكلي</span>
                <span>{totalAmount.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between py-2 mt-2 text-sm text-gray-600">
                <span className="font-semibold">المبلغ المدفوع</span>
                <span>{amountPaid.toFixed(2)} ر.س</span>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Portal Access - QR Code */}
        {patientCredentials?.access_token && (
          <div className="p-6 border-t-2 border-blue-200 bg-blue-50">
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <QRCode
                  value={`https://listechs.com/patient/qr/${patientCredentials.access_token}`}
                  size={120}
                  level="M"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-800 mb-2 text-lg">
                  اطلع على نتائجك إلكترونياً
                </h3>
                <p className="text-sm text-blue-700 mb-1">
                  امسح الرمز بكاميرا جوالك للدخول المباشر لنتائج تحاليلك
                </p>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-blue-600">
                    أو زُر <span className="font-mono font-bold" dir="ltr">listechs.com</span> وأدخل رقم الهوية ورقم الجوال
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thank You Message */}
        <div className="p-6 text-center border-t border-gray-200">
          <p className="text-lg font-semibold text-gray-700">
            {labSettings.invoice_thank_you_message} ({labSettings.invoice_thank_you_message_en})
          </p>
        </div>

        {/* Footer - Custom Image from settings or default */}
        {labSettings.invoice_footer_image ? (
          <div className="print:bg-white">
            <img 
              src={labSettings.invoice_footer_image} 
              alt="Invoice Footer" 
              className="w-full h-auto"
            />
          </div>
        ) : (
          <div className="bg-gray-800 text-white p-6 print:bg-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📞</span>
                <span className="font-bold text-lg" dir="ltr">{labSettings.phone}</span>
              </div>
              
              <div className="text-center flex-1">
                <p className="font-bold mb-2">{tenantData?.name || labSettings.lab_name || ''}</p>
                <p className="text-sm">{tenantData?.nameEn || labSettings.lab_name_en || ''}</p>
              </div>
              
              <div className="flex-shrink-0">
                <img src={tenantData?.branding?.logoUrl || '/listech-logo.svg'} alt={tenantData?.name || labSettings.lab_name || 'Lab'} className="h-12 brightness-0 invert" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4;
            margin: 5mm;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:bg-gray-800 {
            background-color: #1f2937 !important;
            -webkit-print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;

